import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import type { SmsCheck, SmsSettings, SmsSettingsInput } from "@/lib/sms-settings";

/**
 * core-api's `/integrations/sms` (ADR-099 in platform). Admins only, as the routes are. A superadmin
 * with more than one tenant names the tenant; everybody else gets their own.
 */

/** A tenant's SMS settings, the two URLs Twilio calls, and its numbers. */
export function useSmsSettings(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["sms-settings", tenantId ?? "own"]),
    queryFn: () => api.get<SmsSettings>(`/integrations/sms${qs({ tenant_id: tenantId })}`),
    enabled,
  });
}

/**
 * Save the settings. core-api asks Twilio about the keys first, so this can take a second or two,
 * and makes the two triggers when they are missing: the Triggers page is refetched for them.
 */
export function useSaveSmsSettings(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SmsSettingsInput) =>
      api.put<SmsSettings>("/integrations/sms", tenantId ? { ...input, tenant_id: tenantId } : input),
    onSuccess: (saved) => {
      qc.setQueryData(keys.scoped(["sms-settings", tenantId ?? "own"]), saved);
      void qc.invalidateQueries({ queryKey: keys.scoped(["triggers"]) });
    },
  });
}

/** Switch one of the tenant's numbers on or off for texts. The answer is the settings again. */
export function useSetSmsNumber(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sms }: { id: number; sms: boolean }) =>
      api.put<SmsSettings>(`/integrations/sms/numbers/${id}${qs({ tenant_id: tenantId })}`, { sms }),
    onSuccess: (saved) => {
      qc.setQueryData(keys.scoped(["sms-settings", tenantId ?? "own"]), saved);
    },
  });
}

/** Read the Twilio account and say what is still wrong. Changes nothing, here or at Twilio. */
export function useSmsCheck(tenantId?: number) {
  return useMutation({
    mutationFn: () => api.post<SmsCheck>(`/integrations/sms/check${qs({ tenant_id: tenantId })}`),
  });
}
